import { calculatePoints, ExternalAction, selectFinal } from 'components-machine';
import { nanoid } from 'nanoid';
import type { Poll } from 'schema/poll';
import { clearTokenInStorage } from 'src/contexts/errorTrackingContext';
import { sendError } from 'src/sentry';
import { invokeWebviewDeeplink } from '../../utils/invokeWebAction';
import { inIframe, isWebview, resolveLinkUrl } from '../../utils/location';
import { notifyParent } from '../../utils/notifyIframe';
import { isWebviewTemplate } from '../../utils/template';

type PickActionByType<T extends ExternalAction['type'], A extends ExternalAction = ExternalAction> = A extends unknown
  ? A extends { type: T }
    ? A
    : never
  : never;

type Handlers = {
  [Action in ExternalAction['type']]: (args: PickActionByType<Action> & { poll: Poll }) => unknown;
};

const handlers: Handlers = {
  load: async () => {},
  autoclose: async ({ poll, state, unsafe, eventId }) => {
    if (inIframe({ ignoreForce: true })) {
      if (unsafe) {
        notifyParent({ type: 'survey_iframe_requests_close_unsafe', finished: !!state.finishedAt, eventId });
      } else {
        notifyParent({ type: 'survey_iframe_requests_close', status: 'done', finished: !!state.finishedAt, eventId });
      }
      return;
    }

    if (unsafe) return;

    if (state.finalId) {
      const final = selectFinal(poll, state.finalId);
      const redirectUrl = resolveLinkUrl(final?.data.autoCloseRedirectLink);

      if (final?.data.hasAutoClose && redirectUrl) {
        if (isWebviewTemplate(poll.template)) {
          void invokeWebviewDeeplink('location', {
            method: 'deeplink',
            query: `?deeplink=${encodeURIComponent(redirectUrl)}`,
          });
        } else {
          window.location.href = redirectUrl;
        }
        return;
      }
    }

    if (isWebviewTemplate(poll.template)) {
      await invokeWebviewDeeplink('heuristic', { method: 'complete' }, { method: 'close' });
      return;
    }

    window.close();
  },

  close: async ({ poll, state, unsafe, eventId }) => {
    if (inIframe({ ignoreForce: true })) {
      if (unsafe) {
        notifyParent({ type: 'survey_iframe_requests_close_unsafe', finished: !!state.finishedAt, eventId });
      } else {
        notifyParent({ type: 'survey_iframe_requests_close', status: 'done', finished: !!state.finishedAt, eventId });
      }
      return;
    }

    if (unsafe) return;

    if (isWebviewTemplate(poll.template)) {
      await invokeWebviewDeeplink('location', { method: 'close' });
      return;
    }

    window.close();
  },

  done_requests: () => {
    notifyParent({ type: 'survey_iframe_done_requests', status: 'done' });
  },

  done: ({ state }) => {
    notifyParent({ type: 'survey_iframe_done', status: 'done', collected_score: calculatePoints(state.answersStack) });
  },

  wrong_configuration: () => {
    notifyParent({ type: 'survey_iframe_readystatechange', state: 'cancelled', reason: 'INCORRECT_CONFIGURATION' });
  },

  network_error: () => {
    notifyParent({ type: 'survey_iframe_readystatechange', state: 'paused', reason: 'TRACKING_ERROR' });
  },

  ready_after_error: () => {
    notifyParent({ type: 'survey_iframe_readystatechange', state: 'ready' });
  },

  completed: () => {
    notifyParent({ type: 'survey_iframe_readystatechange', state: 'cancelled', reason: 'WITHDRAWN' });
  },

  passed: () => {
    notifyParent({ type: 'survey_iframe_readystatechange', state: 'cancelled', reason: 'COMPLETED' });
  },

  overall: ({ overallResult }) => {
    notifyParent({
      type: 'survey_iframe_result',
      state: 'done',
      result: overallResult,
    });
  },

  ready: () => {
    /* хотя машина может быть готова принимать запросы, компонент вопроса/welcome может не быть отрендерен,
    поэтому событие отправляется в useEffect и зависит от рендера, см. ReadyStateChangeMarker */
  },

  start: () => {
    void import('../../performanceRum').then(({ setupWebPerformanceRum }) => {
      setupWebPerformanceRum(sendError);
    });
  },

  deeplink: ({ deeplink, state }) => {
    const payloadList = Array.isArray(deeplink.payload) ? deeplink.payload : [deeplink.payload];

    if (isWebview({ ignoreForce: true })) {
      invokeWebviewDeeplink(deeplink.by, ...payloadList).then(
        () => deeplink.onResolve?.(),
        () => deeplink.onResolve?.(),
      );
      return;
    }

    // обработка событий для 'webview' опроса открытого вне приложения
    for (const payload of payloadList) {
      switch (payload.method) {
        case 'deeplink': {
          const link = payload.query.replace('?deeplink=', '');
          if (link) window.open(decodeURIComponent(link), '_blank');
          break;
        }
        case 'close': {
          if (inIframe({ ignoreForce: true })) {
            notifyParent({
              type: 'survey_iframe_requests_close',
              status: 'done',
              finished: !!state.finishedAt,
              eventId: nanoid(10),
            });
          } else {
            window.close();
          }
          break;
        }
        default:
          break;
      }
    }

    deeplink.onResolve?.();
  },

  clear_token_in_storage: () => {
    clearTokenInStorage();
  },

  changeAnswer: ({ questionId, answeredIds, answersOrder }) => {
    notifyParent({ type: 'survey_iframe_change_answer', questionId, answeredIds, answersOrder });
  },
};

export const invokeActionHandler = (action: ExternalAction) => {
  switch (action.type) {
    case 'close':
      handlers[action.type](action);
      break;
    case 'autoclose':
      handlers[action.type](action);
      break;
    case 'start':
      handlers[action.type](action);
      break;
    case 'load':
      handlers[action.type](action);
      break;
    case 'done':
      handlers[action.type](action);
      break;
    case 'done_requests':
      handlers[action.type](action);
      break;
    case 'wrong_configuration':
      handlers[action.type](action);
      break;
    case 'completed':
      handlers[action.type](action);
      break;
    case 'passed':
      handlers[action.type](action);
      break;
    case 'ready':
      handlers[action.type](action);
      break;
    case 'network_error':
      handlers[action.type](action);
      break;
    case 'ready_after_error':
      handlers[action.type](action);
      break;
    case 'clear_token_in_storage':
      handlers[action.type](action);
      break;
    case 'overall':
      handlers[action.type](action);
      break;
    case 'deeplink':
      handlers[action.type](action);
      break;
    case 'changeAnswer':
      handlers[action.type](action);
      break;
  }
};
